import { forwardRef } from 'react';

import styled, { css } from 'styled-components';

import {
  SelectSkeletonContainerProps,
  SELECT_SKELETON_MENU_POSITIONS,
} from './SelectSkeleton.decl';

import { Box, Spacer, Typography } from '@aircall/tractor';
import { ComponentSizes } from '@aircall/tractor/es/declarations/common.decl';
import { Chevron } from '@components/Chevron';
import { color, variable } from '@helpers/style.helpers';

const StyledChevron = styled(Chevron)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

interface StyledSelectSkeletonContainerProps
  extends Pick<SelectSkeletonContainerProps, 'isOpen' | 'menuPosition' | 'backgroundColor'> {
  disabled: boolean;
  inputSize: ComponentSizes;
}

const StyledSelectSkeletonContainer = styled(Box)<StyledSelectSkeletonContainerProps>`
  position: relative;
  display: inline-block;
  overflow: hidden;
  height: ${({ inputSize }) => variable.textFieldInput.sizes[inputSize].height};
  box-sizing: border-box;
  border: 1px solid ${color.grey.base};
  border-radius: 8px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  ${({ isOpen, menuPosition }) =>
    isOpen &&
    css`
      border-color: ${color.grey.dark};
      border-top-color: ${menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP
        ? 'transparent'
        : undefined};
      border-bottom-color: ${menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP
        ? undefined
        : 'transparent'};
      border-radius: ${menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP
        ? '0 0 8px 8px'
        : '8px 8px 0 0'};
    `};
`;

export const SelectSkeletonContainer = forwardRef<HTMLDivElement, SelectSkeletonContainerProps>(
  (
    {
      label,
      name,
      children,
      disabled,
      'data-test': dataTest,
      menuPosition,
      hideChevron,
      size,
      backgroundColor,
      ...boxProps
    },
    ref
  ) => {
    function renderContainer() {
      return (
        <StyledSelectSkeletonContainer
          disabled={disabled}
          ref={ref}
          data-test={dataTest}
          menuPosition={menuPosition}
          inputSize={size}
          backgroundColor={backgroundColor}
          {...boxProps}
        >
          {children}
          {!hideChevron && (
            <StyledChevron
              direction={menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP ? 'up' : 'down'}
              size={16}
              color={disabled ? 'grey.light' : 'grey.dark'}
            />
          )}
        </StyledSelectSkeletonContainer>
      );
    }

    return label ? (
      <Spacer space='xxs' direction='vertical' fluid>
        <Typography variant='body' as='label' htmlFor={name} data-test={`${dataTest}-label`}>
          {label}
        </Typography>
        {renderContainer()}
      </Spacer>
    ) : (
      renderContainer()
    );
  }
);
